import { Alert, Card, Descriptions, Typography } from 'antd';
import { dateText, display } from './contracts';
import type { Benefit, RuntimePolicy, OrderAdmissionTierEvidence } from './contracts';

export function OrderAdmissionTierPolicy({ evidence }: { evidence?: OrderAdmissionTierEvidence | null }) {
  const policy = evidence?.supportedPolicy;
  const acl = (value: unknown) => value === null ? '全部受支援項目' : Array.isArray(value) ? value.join('、') || '禁止所有項目' : '尚未提供';
  return <Card size="small" title="建單前展示的 Tier 政策" style={{ marginTop: 12 }}>
    {evidence ? <Descriptions column={1} items={[
      { key: 'time', label: '展示時間', children: dateText(evidence.observedAt) },
      { key: 'tier', label: '等級／政策版本', children: `${evidence.tierId} / ${evidence.policyRevision}` },
      { key: 'devices', label: '裝置／邏輯連線上限', children: display(policy?.maxDevices) },
      { key: 'speed', label: '下載／上傳 Mbps', children: `${display(policy?.downloadLimitMbps)} / ${display(policy?.uploadLimitMbps)}` },
      { key: 'nodes', label: '展示的節點', children: acl(policy?.allowedNodeIds) },
      { key: 'protocols', label: '展示的協議', children: acl(policy?.allowedProtocols) },
    ]} /> : <Typography.Paragraph type="secondary">原單未留存這份展示紀錄。</Typography.Paragraph>}
    <Typography.Paragraph type="secondary">此處只供查核建單前展示內容；會員目前執行政策依目前綁定 Tier，原價格、期限與配額依原單保留。</Typography.Paragraph>
  </Card>;
}

export function archivedTierPolicy(benefit: Pick<Benefit, 'archivedPublicationPolicy' | 'runtimePolicy' | 'tierPolicy' | 'snapshotStatus'>) {
  if (benefit.archivedPublicationPolicy) return benefit.archivedPublicationPolicy;
  // Old APIs expose only the publication snapshot. A live policy can never be
  // substituted when the original evidence is missing.
  return benefit.runtimePolicy?.source !== 'LIVE_TIER' && benefit.snapshotStatus === 'CAPTURED' ? benefit.tierPolicy : null;
}

export function RuntimeTierPolicy({ runtimePolicy, policy }: { runtimePolicy?: RuntimePolicy | null; policy?: Record<string, unknown> | null }) {
  const aclText = (value: unknown, noun: string) => value === null || (Array.isArray(value) && value.length === 1 && value[0] === '*') ? `全部受支援${noun}` : Array.isArray(value) ? value.join('、') || `禁止所有${noun}` : '尚未提供';
  return <Card size="small" title="目前執行政策" style={{ marginTop: 12 }}>
    {runtimePolicy?.source === 'LIVE_TIER' ? <>
      <Typography.Paragraph>目前綁定等級 #{runtimePolicy.tierId}，政策版本 {runtimePolicy.policyRevision}。查詢時間：{dateText(runtimePolicy.asOf)}</Typography.Paragraph>
      {policy && <Descriptions column={1} bordered items={[
        { key: 'name', label: '目前等級', children: display(policy.name) },
        { key: 'devices', label: '裝置／邏輯連線上限', children: display(policy.maxDevices) },
        { key: 'speed', label: '下載／上傳 Mbps', children: `${display(policy.downloadLimitMbps)} / ${display(policy.uploadLimitMbps)}（0 為不限速）` },
        { key: 'nodes', label: '目前允許節點', children: aclText(policy.allowedNodeIds, '節點') },
        { key: 'protocols', label: '目前允許協議', children: aclText(policy.allowedProtocols, '協議') },
      ]} />}
      <Typography.Paragraph type="secondary">既有及新訂閱都依目前 Tier 執行；政策變更可能需要重新連線。原單價格、訂閱期限與期間配額保留。</Typography.Paragraph>
    </> : <Alert showIcon type="info" message="尚未取得目前 Tier 執行政策" description="下方原始紀錄僅供查核，不代表會員目前可用的節點、裝置或速率。" />}
  </Card>;
}
